import * as actionTypes from "./actionTypes";
import {
  getDigitalWarehouseAPI,
  deleteDigitalWarehouseItemAPI,
  createDigitalWarehouseItemAPI,
  modifyDigitalWarehouseItemAPI,
  downloadDigitalWarehouseItemApi,
  syncDigitalWarehouseFilesAPI,
} from "@icarius-connection/api";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";
import { downloadBlob } from "@icarius-utils/download";
import { getFileExtension, getFileName } from "@icarius-utils/fileUpload";

const errorHandler = (e, dispatch) => {
  let errorString = "";
  switch (e.response?.data?.status) {
    case "INVALID_DATA":
    case "VALIDATION_ERROR":
      errorString = getLocalizedString("validationError");
      break;
    case "DUPLICATED_DATA":
      errorString = getLocalizedString("fileOnFolderAlreadyExists");
      break;
    case "IN_USE":
      errorString = getLocalizedString("cantDeleteFileInUse");
      break;
    default:
      errorString = getErrorStringFromServerError(e);
      break;
  }
  dispatch(openSnackbarAction({ msg: errorString, severity: "error" }));
}

export const getDigitalWarehouseAction = (query) => async (dispatch) => {
  dispatch({ type: actionTypes.GET });
  try {
    let response = await getDigitalWarehouseAPI(query);

    let data = response.data && response.data.data;
    let permissionList = response.data && response.data.permissionsList;
    let typeList = response.data && response.data.typeLists;
    let hasBotAssistant = response.data && response.data.hasBotAssistant;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GET_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GET_FULFILLED,
      payload: {
        data,
        permissionList,
        typeList,
        hasBotAssistant
      },
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const deleteDigitalWarehouseItemAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE });
  try {
    let response = await deleteDigitalWarehouseItemAPI(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("fileDeletedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const createDigitalWarehouseItemAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE });
  try {
    let formData = new FormData();
    formData.append('type', dataToSend.type);
    formData.append('description', dataToSend.description);
    formData.append('groupCode', dataToSend.groupCode || "");
    formData.append('folder', dataToSend.folder);
    formData.append('sfolder', dataToSend.sfolder);
    formData.append('year', dataToSend.year);
    formData.append('month', dataToSend.month);
    formData.append('expirationDate', dataToSend.expirationDate || "");
    formData.append('permissions', dataToSend.permission);
    dataToSend.file.forEach((file) => {
      formData.append('FILE', file);
    })

    let response = await createDigitalWarehouseItemAPI(formData);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.CREATE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.CREATE_FULFILLED,
      payload: { data },
    });

    const message = (() => {
      if (dataToSend.file.length === 1) return getLocalizedString("fileCreatedSuccessfully");
      return `${dataToSend.file.length} ${getLocalizedString("filesCreatedSuccessfully")}`;
    })();

    dispatch(openSnackbarAction({ msg: message, severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e?.response;
  }
};

export const modifyDigitalWarehouseItemAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.MODIFY });
  try {
    let formData = new FormData();
    formData.append('FILE', dataToSend.file);
    formData.append('type', dataToSend.type);
    formData.append('description', dataToSend.description);
    formData.append('groupCode', dataToSend.groupCode || "");
    formData.append('folder', dataToSend.folder);
    formData.append('sfolder', dataToSend.sfolder);
    formData.append('year', dataToSend.year);
    formData.append('month', dataToSend.month);
    formData.append('expirationDate', dataToSend.expirationDate || "");
    formData.append('permissions', dataToSend.permission);
    formData.append('internalCode', dataToSend.internalCode);

    let response = await modifyDigitalWarehouseItemAPI(formData);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.MODIFY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.MODIFY_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("fileEditedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.MODIFY_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e?.response;
  }
};

export const syncDigitalWarehouseFilesAction = (dataToSend) => async (dispatch) => {
  dispatch({ type: actionTypes.SYNC });
  try {
    let response = await syncDigitalWarehouseFilesAPI(dataToSend);

    let data = response.data && response.data.data;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.SYNC_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.SYNC_FULFILLED,
      payload: { data },
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("filesSyncedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.SYNC_REJECTED, payload: e });
    errorHandler(e, dispatch);
    return e?.response;
  }
};

export const downloadDigitalWarehouseItemAction = (code) => async (dispatch) => {
  dispatch({ type: actionTypes.DOWNLOAD });
  try {
    let response = await downloadDigitalWarehouseItemApi(code);
    let title = response.headers["x-content-namefile"];

    if (response.status !== 200) {
      dispatch({ type: actionTypes.DOWNLOAD_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DOWNLOAD_FULFILLED,
    });

    downloadBlob(response.data, getFileExtension(title), getFileName(title));

    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DOWNLOAD_REJECTED });
    dispatch(openSnackbarAction({ msg: getErrorStringFromServerError(e), severity: "error" }));
  }
};
